import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { Hero as HeroModule } from "@bw/modules"
import { Button, CmsImage } from "@bw/bits"

const HeroCarousel = ({ slides, fullPage }) => {
  const carouselRef = React.useRef(null)
  const slidesRef = React.useRef([])
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const [playing, setPlaying] = React.useState(true)
  const [ready, setReady] = React.useState(false)

  /**
   * Run the timer
   */
  React.useEffect(() => {
    const timer = setInterval(() => {
      if (playing) {
        setCurrentIndex(prev => {
          return prev + 1 > slides.length - 1 ? 0 : prev + 1
        })
      }
    }, 6000)

    return () => {
      clearInterval(timer)
    }
  }, [slides.length, setCurrentIndex, playing])

  /**
   * Set the height of the carousel
   */
  React.useEffect(() => {
    const timer = setTimeout(
      () => {
        const { height } =
          slidesRef.current[currentIndex].getBoundingClientRect()
        carouselRef.current.style.height = `${height}px`
        if (!ready) {
          setReady(true)
        }
      },
      ready ? 0 : 1000
    )

    return () => {
      clearTimeout(timer)
    }
  }, [currentIndex, slidesRef])

  /**
   * Stop playing the carousel under certain conditions
   */
  React.useEffect(() => {
    const scrolling = () => {
      setPlaying(window.scrollY < 40)
    }

    scrolling()
    window.addEventListener("scroll", scrolling, { passive: true })

    return () => {
      window.removeEventListener("scroll", scrolling)
    }
  }, [setPlaying])

  return (
    <Carousel ref={carouselRef} ready={ready}>
      {slides.map(({ suptitle, title, subtitle, image, buttons }, k) => (
        <Slide
          key={k}
          ref={el => (slidesRef.current[k] = el)}
          className={currentIndex === k ? "active" : ""}
        >
          <HeroModule
            {...{ suptitle, title, subtitle, fullPage, noH1Tag: k > 0 }}
            image={<CmsImage src={image} alt={image.alt} />}
            focus={image.has_focus ? image.focus_css : undefined}
            buttons={buttons.map((button, i) => {
              if (button.linkType.value === "url" && button.url) {
                return (
                  <Button
                    key={i}
                    label={button.linkText}
                    href={button.url}
                    external={button.isExternal}
                    modal={button.openInModal}
                    primary
                    inversed={i !== 0}
                  />
                )
              }

              if (button.linkType.value === "entry" && button.entry) {
                return (
                  <Button
                    key={i}
                    label={button.linkText}
                    to={button.entry.url}
                    primary
                    inversed={i !== 0}
                  />
                )
              }

              return null
            })}
          />
        </Slide>
      ))}
    </Carousel>
  )
}

const Carousel = styled.div`
  position: relative;
  width: 100%;
  transition: height 0.9s;

  ${props =>
    !props.ready &&
    css`
      > div {
        display: none;
        position: relative;
      }

      > div:first-child {
        display: block;
      }
    `}
`

const Slide = styled.div`
  position: absolute;
  width: 100%;
  opacity: 0;
  pointer-events: none;
  transition: opacity 2s;

  &.active {
    opacity: 1;
    pointer-events: all;
  }
`

HeroCarousel.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.object),
  fullPage: PropTypes.bool,
}

export { HeroCarousel }
